import React, { useEffect } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Select,
  Button,
  useToast,
  PhoneNumber,
} from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { useUser } from '../../../hooks/graphql/users/useUser';
import { AdminLockOverlay } from '../client/AdminLockOverlay';
import { US_STATES } from '../../../constants/states';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  organization: Organization | null;
  onSuccess?: () => void;
}

const profileSchema = z.object({
  remi_intro_name: z.string().min(1, 'Remi introduction name is required'),
  org_phone: z.string().refine((v) => v.replace(/\D/g, '').length >= 10, 'Valid phone number is required'),
  email: z.string().email('Invalid email address'),
  notification_email: z.string().email('Invalid email address'),
  street: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(1, 'Zip code is required'),
  country: z.string().min(1, 'Country is required'),
  timezone: z.string().min(1, 'Timezone is required'),
});

type FormData = z.infer<typeof profileSchema>;

export const ProfileModal: React.FC<ProfileModalProps> = ({ isOpen, onClose, organization, onSuccess }) => {
  const { updateSettings, isPending } = useUpdateOrganizationSettings();
  const { isAdmin } = useUser();
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      remi_intro_name: '',
      org_phone: '',
      email: '',
      notification_email: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      timezone: '',
    },
  });

  useEffect(() => {
    if (isOpen && organization) {
      reset({
        remi_intro_name: organization.remi_intro_name || organization.org_name || '',
        org_phone: organization.org_phone || '',
        email: organization.email || '',
        notification_email: organization.notification_email || '',
        street: organization.client_address?.street || '',
        city: organization.client_address?.city || '',
        state: organization.client_address?.state || '',
        zip: organization.client_address?.zip || '',
        country: organization.client_address?.country || 'US',
        timezone: organization.timezone || '',
      });
    }
  }, [isOpen, organization, reset]);

  const onSubmit = async (data: FormData) => {
    if (!organization) return;

    try {
      await updateSettings({
        id: organization.id,
        data: {
          remi_intro_name: data.remi_intro_name,
          org_phone: data.org_phone,
          email: data.email,
          notification_email: data.notification_email,
          timezone: data.timezone,
          client_address: {
            street: data.street,
            city: data.city,
            state: data.state,
            zip: data.zip,
            country: data.country,
          } as any,
        },
      });

      showToast({
        message: 'Profile updated successfully',
        variant: 'success',
      });
      onSuccess?.();
      onClose();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update profile',
        variant: 'error',
      });
    }
  };

  if (!organization) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Organization Profile</Typography>
      </Modal.Header>
      <Modal.Body>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <AdminLockOverlay locked={!isAdmin} subject="Organization profile">
          {/* Organization Information */}
          <div className="bg-muted dark:bg-white/5 rounded-lg p-6 border border-border/50 dark:border-white/10 space-y-3">
            <Form.Control required>
              <Form.Label>How should Remi introduce your company?</Form.Label>
              <Input {...register('remi_intro_name')} error={errors.remi_intro_name?.message} />
            </Form.Control>

            <div className="grid grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>What is your office phone number?</Form.Label>
                <Controller
                  control={control}
                  name="org_phone"
                  render={({ field }) => (
                    <PhoneNumber
                      value={field.value}
                      onChange={(val) => field.onChange(val ?? '')}
                      onBlur={field.onBlur}
                      defaultCountry="US"
                      error={errors.org_phone?.message}
                      disabled={!isAdmin}
                    />
                  )}
                />
              </Form.Control>

              <Form.Control required>
                <Form.Label>What time zone are you in?</Form.Label>
                <Controller
                  control={control}
                  name="timezone"
                  render={({ field }) => (
                    <Select
                      {...field}
                      error={errors.timezone?.message}
                      options={TIMEZONE_OPTIONS}
                      placeholder="Select your timezone"
                      searchable
                      disabled={!isAdmin}
                    />
                  )}
                />
              </Form.Control>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>What is your office email?</Form.Label>
                <Input
                  {...register('email')}
                  type="email"
                  placeholder="contact@company.com"
                  error={errors.email?.message}
                />
              </Form.Control>

              <Form.Control required>
                <Form.Label>Email address for Notifications</Form.Label>
                <Input
                  {...register('notification_email')}
                  type="email"
                  placeholder="notifications@company.com"
                  error={errors.notification_email?.message}
                />
              </Form.Control>
            </div>
          </div>

          {/* Address Information */}
          <div className="bg-muted dark:bg-white/5 rounded-lg p-6 border border-border/50 dark:border-white/10 space-y-3">
            <Typography variant="h3" className="text-sm font-semibold text-foreground mb-4">
              What is your office address?
            </Typography>

            <Form.Control required>
              <Form.Label>Street Address</Form.Label>
              <Input
                {...register('street')}
                placeholder="123 Main Street"
                error={errors.street?.message}
              />
            </Form.Control>

            <div className="grid grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>City</Form.Label>
                <Input
                  {...register('city')}
                  placeholder="San Francisco"
                  error={errors.city?.message}
                />
              </Form.Control>

              <Form.Control required>
                <Form.Label>State</Form.Label>
                <Controller
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <Select
                      {...field}
                      error={errors.state?.message}
                      options={US_STATES}
                      placeholder="Select state"
                      searchable
                      disabled={!isAdmin}
                    />
                  )}
                />
              </Form.Control>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>Zip Code</Form.Label>
                <Input {...register('zip')} placeholder="94102" error={errors.zip?.message} />
              </Form.Control>

              <Form.Control required>
                <Form.Label>Country</Form.Label>
                <Controller
                  control={control}
                  name="country"
                  render={({ field }) => (
                    <Select
                      {...field}
                      error={errors.country?.message}
                      options={[
                        { value: 'US', label: 'United States' },
                        { value: 'Canada', label: 'Canada' },
                      ]}
                      placeholder="Select country"
                      disabled={!isAdmin}
                    />
                  )}
                />
              </Form.Control>
            </div>
          </div>
          </AdminLockOverlay>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isPending}
          disabled={isPending || !isAdmin}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
